import { useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

export interface FocusSelection<T> {
  active: T | null;
  index: number;
  total: number;
  hasPrevious: boolean;
  hasNext: boolean;
  open: (id: string) => void;
  select: (id: string) => void;
  goPrevious: () => void;
  goNext: () => void;
  close: () => void;
}

interface Options {
  enabled?: boolean;
  isReady?: boolean;
  paramName?: string;
}

export const useFocusSubjectParam = <T,>(
  items: readonly T[],
  idOf: (item: T) => string,
  { enabled = true, isReady = false, paramName = "focus" }: Options = {},
): FocusSelection<T> => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedId = enabled ? searchParams.get(paramName) : null;

  const index = useMemo(
    () => (requestedId == null ? -1 : items.findIndex((item) => idOf(item) === requestedId)),
    [items, idOf, requestedId],
  );

  const write = useCallback(
    (id: string | null, replace: boolean) => {
      const next = new URLSearchParams(searchParams);
      if (id == null) next.delete(paramName);
      else next.set(paramName, id);
      setSearchParams(next, { replace });
    },
    [searchParams, setSearchParams, paramName],
  );

  useEffect(() => {
    if (enabled && isReady && requestedId != null && index === -1) write(null, true);
  }, [enabled, isReady, requestedId, index, write]);

  const goTo = useCallback((offset: number) => {
    const target = items[index + offset];
    if (target) write(idOf(target), true);
  }, [items, idOf, index, write]);

  return {
    active: index === -1 ? null : items[index],
    index,
    total: items.length,
    hasPrevious: index > 0,
    hasNext: index !== -1 && index < items.length - 1,
    open: useCallback((id: string) => { if (enabled) write(id, false); }, [enabled, write]),
    select: useCallback((id: string) => { if (enabled) write(id, true); }, [enabled, write]),
    goPrevious: useCallback(() => goTo(-1), [goTo]),
    goNext: useCallback(() => goTo(1), [goTo]),
    close: useCallback(() => write(null, true), [write]),
  };
};
